<template>
  <div data-testid="showcase-empty-state" class="border-2 border-dashed border-gray-300 rounded-lg p-4 lg:p-6 mb-8">
    <h3 class="m-0 mb-4 lg:mb-5 ds-text-xl text-gray-700 font-bold flex items-center gap-2">
      Add a Pinned Playlist
      <span
        class="inline-block m-0 h-4 px-1 font-bold text-white rounded-sm ds-text-2xs ds-label bg-cyan-300">Beta</span>
    </h3>
    <div class="grid grid-cols-1 lg:grid-cols-2 grid-rows-auto lg:grid-rows-1 items-center  gap-4">
      <div class="w-full h-full bg-cyan-100 rounded-lg p-6 flex items-center justify-center">
        <div class="w-full h-auto rounded-lg aspect-video max-w-md">
          <div class="wistia_responsive_padding" style="padding:56.25% 0 0 0;position:relative;">
            <div class="wistia_responsive_wrapper" style="height:100%;left:0;position:absolute;top:0;width:100%;">
              <iframe :src="videoSrc" title="New Feature: Pinned Playlist Video" allow="autoplay; fullscreen"
                allowtransparency="true" frameborder="0" scrolling="no" class="wistia_embed" name="wistia_embed"
                msallowfullscreen width="100%" height="100%"></iframe>
            </div>
          </div>
        </div>
      </div>
      <div class="text-gray-700 lg:p-3">
        <p class="m-0 mb-4 ds-text-base font-normal">
          Create a pinned playlist to impress college coaches on each profile view.
        </p>
        <div class="flex items-baseline gap-2 mb-3">
          <Icon icon="checkmark" color="green" class="ds-text-xl m-0" />
          <p class="m-0">Your best plays at the top of your profile, grouped by skill</p>
        </div>
        <div class="flex items-baseline gap-2 mb-3">
          <Icon icon="checkmark" color="green" class="ds-text-xl m-0" />
          <p class="m-0">
            No music, no cards, no special effects – just pure highlights, the way coaches want them
          </p>
        </div>
        <div class="flex items-baseline gap-2 mb-4">
          <Icon icon="checkmark" color="green" class="ds-text-xl m-0" />
          <p class="m-0">Quickly add and swap clips to keep your playlist fresh throughout the season</p>
        </div>
        <div class="hidden lg:flex gap-2 mt-5">
          <div v-if="hasUploadedVideo">
            <a data-testid="go-to-playlist-editor" class="cursor-pointer text-blue-300 hover:text-blue-400 font-medium"
              role="button" @click="goToPlaylistEditor">
              Go to playlist editor
              <Icon icon="arrow-right" />
            </a>
          </div>
          <div v-else data-testid="no-video-contextual-message"
            class="p-3 flex flex-row items-center justify-between rounded font-normal w-full ds-text-sm text-orange-300 bg-orange-100">
            <Icon icon="info-circle" color="orange" class="ds-text-xs mr-1" />
            <p class="w-full m-0">
              <span data-testid="upload-video-cta" class="underline cursor-pointer"
                @click="goToUploadVideo">Upload</span> or
              <span data-testid="connect-video-cta" class="underline cursor-pointer"
                @click="goToConnectVideo">connect</span> video to create clips and start your playlist
            </p>
          </div>
        </div>
        <div class="block lg:hidden mt-5">
          <ContextualMessage
            message="Playlist creation is only available on larger screens. Please increase your screen width or use a larger device to get started."
            :dismissable="false" color="orange" />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { defineComponent } from 'vue'
import { ContextualMessage, Icon } from '@sportsrecruits/playbook';
import buildUrl from "@/utils/app-url";
import { mapGetters } from "vuex";

export default defineComponent({
  name: 'AthleteProfileShowcaseEmptyState',
  components: {
    ContextualMessage,
    Icon,
  },
  computed: {
    ...mapGetters('videoLibrary', ['hasUploadedVideo']),
    ...mapGetters('userData', ['getUserSport']),
    videoSrc() {
      return this.getVideoSrc();
    },
  },
  methods: {
    goToPlaylistEditor() {
      window.location.href = this.getPlaylistManagerUrl()
    },
    goToUploadVideo() {
      window.location.href = this.getUploadVideoUrl()
    },
    goToConnectVideo() {
      window.location.href = this.getConnectVideoUrl()
    },
    getPlaylistManagerUrl() {
      const path = '/video/playlists/pinned';
      return buildUrl(path);
    },
    getUploadVideoUrl() {
      const path = '/video?upload-video=true'
      return buildUrl(path)
    },
    getConnectVideoUrl() {
      const path = '/video?connect-video=true'
      return buildUrl(path)
    },
    getVideoSrc() {
      switch (this.getUserSport) {
        case 'basketball':
          return 'https://fast.wistia.net/embed/iframe/tv0jswtom5?seo=false&videoFoam=true';
        case 'softball':
        case 'baseball':
          return 'https://fast.wistia.net/embed/iframe/onjbrmln13?seo=false&videoFoam=true';
        case 'volleyball':
          return 'https://fast.wistia.net/embed/iframe/bhqv0at45s?seo=false&videoFoam=true';
        case 'soccer':
        case 'lacrosse':
          return 'https://fast.wistia.net/embed/iframe/hq7suhctb9?seo=false&videoFoam=true'
        default:
          return 'https://fast.wistia.net/embed/iframe/hq7suhctb9?seo=false&videoFoam=true';
      }
    },
  },
})
</script>